// Balanced engagement is a score near 1.0 (an equal share of turns). Bands are
// relative to that, not a fixed count, since team size and total turns vary
// meeting to meeting: green 0.75-1.25, yellow 0.5-0.75 / 1.25-2.0, red beyond that.
function bandColor(score) {
  if (score <= 0.5 || score >= 2.0) return '#922b3f'; // red — dominated or checked out
  if (score < 0.75 || score > 1.25) return '#cba43a'; // yellow — too much or too little at times
  return '#5f7942'; // green — balanced engagement
}

export default function FrequencyChart({ frequency, nameMap = {}, printMode = false }) {
  if (!frequency || frequency.length < 2) return null;

  const SIZE = printMode ? 560 : 320;
  const CX = SIZE / 2;
  const CY = SIZE / 2;
  const R_BALANCED = printMode ? 160 : 80;  // radius at score = 1.0 (the reference circle)
  const MAX_R = printMode ? 250 : 130;       // max radius for score approaching 0

  const N = frequency.length;
  const ranked = [...frequency].sort((a, b) => b.score - a.score);
  const points = ranked.map((f, i) => {
    const angle = (2 * Math.PI * i) / N - Math.PI / 2; // start from top
    const r = Math.min(R_BALANCED / (f.score || 0.01), MAX_R);
    const x = CX + r * Math.cos(angle);
    const y = CY + r * Math.sin(angle);
    const name = nameMap[f.speaker] || f.speaker.split('@')[0];
    return { x, y, name, score: f.score, angle };
  });

  return (
    <div style={{
      marginTop: printMode ? 0 : 32,
      width: '100%',
      ...(printMode ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}),
    }}>
      {!printMode && <div className="report-section-header">Speaking Frequency</div>}
      <p style={{
        fontSize: printMode ? 10 : '0.82rem', color: printMode ? '#6b757e' : 'var(--ink-muted)',
        marginBottom: printMode ? 6 : 12, flexShrink: printMode ? 0 : undefined,
      }}>
        Distance from center reflects how often each person spoke relative to an equal share.
      </p>
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        width="100%"
        height={printMode ? '100%' : undefined}
        style={printMode ? { flex: 1, minHeight: 0 } : { maxHeight: 320 }}
      >
        {/* Reference circle at score = 1.0 */}
        <circle cx={CX} cy={CY} r={R_BALANCED} fill="none" stroke="#333" strokeWidth="1.5" />

        {/* Center dot */}
        <circle cx={CX} cy={CY} r="3" fill="#ccc" />

        {/* Phosphor User Fill icons with names */}
        {points.map((p, i) => {
          const dx = p.x - CX;
          const dy = p.y - CY;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const iconSize = printMode ? (N > 10 ? 28 : 36) : (N > 10 ? 16 : 20);
          // In print mode: name always below the icon; otherwise: label placed radially outward
          const tx = p.x - iconSize / 2;
          const ty = p.y - iconSize / 2;
          const scale = iconSize / 256;
          let lx, ly, anchor, fontSize;
          if (printMode) {
            lx = p.x;
            ly = p.y + iconSize / 2 + (N > 10 ? 12 : 14);
            anchor = 'middle';
            fontSize = N > 10 ? 11 : 13;
          } else {
            const offset = iconSize / 2 + 6;
            lx = p.x + (dx / dist) * offset;
            ly = p.y + (dy / dist) * offset;
            anchor = dx > 8 ? 'start' : dx < -8 ? 'end' : 'middle';
            fontSize = N > 10 ? 10 : 13;
          }
          return (
            <g key={i}>
              <g transform={`translate(${tx.toFixed(1)}, ${ty.toFixed(1)}) scale(${scale.toFixed(4)})`}>
                <path
                  d="M230.93,220a8,8,0,0,1-6.93,4H32a8,8,0,0,1-6.92-12c15.23-26.33,38.7-45.21,66.09-54.16a72,72,0,1,1,73.66,0c27.39,8.95,50.86,27.83,66.09,54.16A8,8,0,0,1,230.93,220Z"
                  fill={bandColor(p.score)}
                />
              </g>
              <text
                x={lx.toFixed(1)} y={(ly + (printMode ? 0 : iconSize * 0.5)).toFixed(1)}
                textAnchor={anchor} fontSize={fontSize}
                fill="#2a3d5b" fontWeight="600"
              >
                {p.name}
              </text>
            </g>
          );
        })}

      </svg>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, fontSize: printMode ? 9 : '0.75rem', color: printMode ? '#6b757e' : 'var(--ink-muted)', marginTop: 4, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#5f7942', display: 'inline-block' }} />
          Balanced
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#cba43a', display: 'inline-block' }} />
          Too much or too little at times
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#922b3f', display: 'inline-block' }} />
          Dominated or checked out
        </span>
      </div>
    </div>
  );
}
